import { Share as CapacitorShare } from "@capacitor/share";
import { useMutation } from "@tanstack/react-query";

import { Models } from "@/api/types";
import { generateReferralShareText } from "@/modules/referralRewards";
import { queryClient } from "@/pages/Root";
import {
  getIncentivesByPlatformIdReferralHistoryByPersonId,
  getReferralCodesPersonalCodeByPersonIdShareInvite,
  getReferralCodesStatelessShareUrlByStatelessPrettyToken,
  postReferralCodesPersonalCodeByPersonId,
  postReferralCodesStatelessShareUrlByPersonalReferralCodePlatformByPlatformShareInvite,
} from "api/methods";
import { isNativeMobileApp } from "utils/platform.helpers";

type UseShareReferralRewardParams = {
  platform: Models<"PlatformModel">;
  user: Models<"UserModel1">;
  rewardTitle: string;
  /**
   * Optional per-location share copy template, typically sourced from
   * `DisplayAvailableReferralReward.platformLocationReferralRewardCopyTemplate`.
   * When omitted/empty the default copy is used.
   */
  shareCopyTemplate?: string | null;
};

type UseShareReferralRewardResult = {
  shareReferralReward: () => Promise<string | null>;
};

export const useShareReferralReward = ({
  platform,
  user,
  rewardTitle,
  shareCopyTemplate,
}: UseShareReferralRewardParams): UseShareReferralRewardResult => {
  const referralMutation = useMutation({
    mutationFn: async (personId: string) => {
      await postReferralCodesPersonalCodeByPersonId({
        personId,
      });

      const codes = await getReferralCodesPersonalCodeByPersonIdShareInvite({
        personId,
      });

      const currentPlatformCode = codes.find(
        (code: Models<"PlatformShareInvite">) => code.platformId.value === platform.id,
      );

      if (!currentPlatformCode) {
        throw new Error("Current platform code not found");
      }

      const statelessShareablePrettyToken =
        await postReferralCodesStatelessShareUrlByPersonalReferralCodePlatformByPlatformShareInvite(
          {
            personalReferralCode: currentPlatformCode.personalReferralCode.rawValue,
            platformShareInvite: currentPlatformCode.rawValue,
          },
        );

      const trackedShareUrl = await getReferralCodesStatelessShareUrlByStatelessPrettyToken({
        statelessPrettyToken: statelessShareablePrettyToken,
      });

      return trackedShareUrl.shortenedUrl ?? trackedShareUrl.absoluteUrl;
    },
    onError: (error) => {
      console.error("Error creating referral share text:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getIncentivesByPlatformIdReferralHistoryByPersonId.key({
          platformId: platform.id,
          personId: user.id,
        }),
      });

      queryClient.refetchQueries({
        queryKey: getIncentivesByPlatformIdReferralHistoryByPersonId.key({
          platformId: platform.id,
          personId: user.id,
        }),
      });
    },
  });

  const share = async (urlToShare: string) => {
    const shareText = generateReferralShareText({
      platformName: platform.name ?? "",
      rewardTitle,
      locationName: user.platformLocation?.name ?? "",
      urlToShare,
      template: shareCopyTemplate,
    });

    if (isNativeMobileApp()) {
      try {
        await CapacitorShare.share({
          text: shareText,
        });
      } catch (error) {
        console.error("Error sharing with Capacitor:", error);
      }
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText,
        });
      } catch (error) {
        console.error("Error sharing with navigator.share:", error);
      }
      return;
    }

    // Fallback to email for Windows and other platforms without share support
    globalThis.location.href = `mailto:?body=${encodeURIComponent(shareText)}`;
    console.log(`Fallback email share. Referral URL: ${urlToShare}`);
  };

  const shareReferralReward = async (): Promise<string | null> => {
    try {
      const referralUrl = await referralMutation.mutateAsync(user.id);

      const urlWithLocation = `${referralUrl}`;

      await share(urlWithLocation);
      return urlWithLocation;
    } catch (error) {
      console.error("Error in shareReferralReward:", error);
      return null;
    }
  };

  return {
    shareReferralReward,
  };
};
